// src/services/rosterTransfer.js — the impure write path for roster
// export/import (frontend.md: dataClient is the only door; PB API rules let
// active business members create/update bot + bot_team directly).
//
// Two persistence paths, one public API:
//  • PB workspace (production): writes bot_team/bot rows through the PB SDK
//    (browser → node /pb proxy → loopback PB), mirroring server/seed.js's
//    seedRoster two-pass shape: create teams → create bots with tier relation
//    resolved by slug → second pass wires consultable_bots relations by slug.
//    On any failure the already-created rows are rolled back (deleted) so a
//    failed import never leaves a partial roster.
//  • Demo/local workspace (dev ?demo=1): dataClient.create on the in-memory
//    mock collections — same merge semantics, no PB.
//
// Merge semantics live in rosterBundle.planRosterImport (pure, shared by the
// preview UI and this writer): ADD only, never overwrite live records.

import {dataClient} from '../data/dataClient';
import {pb} from './pb.js';
import {
  buildRosterBundle,
  parseRosterBundle,
  planRosterImport,
  ALIAS_TO_CLIENT,
  slugify
} from './rosterBundle.js';

const BOT_COLORS=['#111827','#5b8def','#62c5a7','#ef806e','#9979dc','#f3c969'];

// ── Export ───────────────────────────────────────────────────────────────────

/** Build (and optionally download) the full-roster transfer bundle. */
export function exportRoster() {
  const bundle=buildRosterBundle(dataClient.peek());
  downloadRosterBundle(bundle);
  return bundle;
}

function downloadRosterBundle(bundle) {
  const name=`bots-${slugify(bundle.source.name || 'business')}-${new Date().toISOString().slice(0,10)}.json`;
  const blob=new Blob([JSON.stringify(bundle,null,2)],{type: 'application/json'});
  const url=URL.createObjectURL(blob);
  const anchor=document.createElement('a');
  anchor.href=url;
  anchor.download=name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ── Import ───────────────────────────────────────────────────────────────────

/**
 * Parse a bundle file and plan it against the CURRENT workspace. No writes —
 * the UI shows this plan for confirmation before importRoster applies it.
 */
export function previewRosterImport(text) {
  const parsed=parseRosterBundle(text);
  return {
    parsed,
    plan: planRosterImport(parsed,dataClient.peek())
  };
}

/**
 * Apply a roster import. Accepts either raw bundle text (re-planned against
 * the current state — the browser may have changed between preview and
 * confirm) or the exact {parsed, plan} pair from previewRosterImport.
 *
 * Returns {importedTeams, importedBots, skippedTeams, skippedBots, notes}.
 */
export async function importRoster(input) {
  const {parsed,plan}=typeof input==='string'
    ? previewRosterImport(input)
    : input;

  if (!plan.newTeams.length && !plan.newBots.length) {
    const nothing=new Error('Nothing to import — every team and bot in this bundle already exists in this workspace.');
    nothing.code='ROSTER_IMPORT_NOTHING_NEW';
    throw nothing;
  }

  const result={
    importedTeams: plan.newTeams.length,
    importedBots: plan.newBots.length,
    skippedTeams: plan.skippedTeams,
    skippedBots: plan.skippedBots,
    notes: plan.notes
  };

  if (dataClient.isPbWorkspace()) {
    await importIntoPocketBase(plan);
    await dataClient.refreshWorkspace();
    return result;
  }

  await importIntoLocalWorkspace(plan);
  return result;
}

// ── PocketBase path ──────────────────────────────────────────────────────────

async function importIntoPocketBase(plan) {
  const businessId=dataClient.peek().business?.id;
  if (!businessId) {
    throw new Error('No active business — sign in before importing a roster.');
  }

  // Tier + module relation ids are per-tenant global rows (ANY_AUTHED reads).
  const tierRows=await pb.collection('model_tier').getFullList({batch: 200});
  const tierIdBySlug=new Map(tierRows.map((row)=> [row.slug,row.id]));
  const moduleRows=await pb.collection('module').getFullList({batch: 200});
  const moduleIdBySlug=new Map(moduleRows.map((row)=> [row.slug,row.id]));

  const created={teams: [],bots: []}; // for rollback

  try {
    // Pass 1 — teams (only bundle-new teams; existing slugs untouched).
    const teamIdBySlug=new Map();
    for (const team of plan.newTeams) {
      const row=await pb.collection('bot_team').create({
        business: businessId,
        slug: team.slug,
        name: team.name,
        module: moduleIdBySlug.get(team.module) || '',
        enabled: team.enabled !== false
      });
      teamIdBySlug.set(team.slug,row.id);
      created.teams.push(row.id);
    }

    // Existing live teams (bots may join them): map slug → live PB record id.
    const liveTeams=await pb.collection('bot_team').getFullList({
      batch: 200,
      filter: `business="${businessId}"`
    });
    for (const live of liveTeams) teamIdBySlug.set(slugify(live.slug),live.id);

    // Pass 2 — bots, then consult wiring (relations need record ids, so the
    // second update pass is unavoidable — same shape as seedRoster).
    const liveBotIdBySlug=new Map(
      (await pb.collection('bot').getFullList({
        batch: 200,
        filter: `business="${businessId}"`
      })).map((row)=> [slugify(row.slug),row.id])
    );

    const botIdBySlug=new Map();
    for (const bot of plan.newBots) {
      const tierSlug=bot.model_tier;
      const tierId=tierIdBySlug.get(tierSlug);
      if (!tierId) throw new Error(`Model tier "${tierSlug}" is not available — import aborted.`);

      const row=await pb.collection('bot').create({
        business: businessId,
        bot_team: bot.team ? (teamIdBySlug.get(bot.team) || '') : '',
        slug: bot.slug,
        name: bot.name,
        role: bot.role,
        system_prompt: bot.system_prompt,
        model_tier: tierId,
        enabled: bot.enabled !== false,
        is_lead: Boolean(bot.is_lead),
        consultable_bots: [],
        agent_class: bot.agent_class,
        tools: bot.tools
      });
      botIdBySlug.set(bot.slug,row.id);
      created.bots.push(row.id);
    }

    // Pass 3 — consultable_bots relations (bundle ∪ live bot slugs).
    for (const bot of plan.newBots) {
      if (!bot.consultable_bots.length) continue;
      const consultIds=bot.consultable_bots
        .map((slug)=> botIdBySlug.get(slug) || liveBotIdBySlug.get(slug))
        .filter(Boolean);
      if (!consultIds.length) continue;
      await pb.collection('bot').update(botIdBySlug.get(bot.slug),{
        consultable_bots: consultIds
      });
    }
  } catch (err) {
    // Roll back everything this import created (bots first — they may
    // reference teams). Live records were never touched, by design.
    for (const id of created.bots.reverse()) {
      await pb.collection('bot').delete(id).catch(()=> {});
    }
    for (const id of created.teams.reverse()) {
      await pb.collection('bot_team').delete(id).catch(()=> {});
    }
    const error=new Error(`Import failed and was rolled back: ${err?.message || err}`);
    error.code='ROSTER_IMPORT_ROLLED_BACK';
    error.cause=err;
    throw error;
  }
}

// ── Demo/local path ──────────────────────────────────────────────────────────

async function importIntoLocalWorkspace(plan) {
  const businessId=dataClient.peek().business?.id || null;

  const teamIdBySlug=new Map();
  for (const team of plan.newTeams) {
    const created=await dataClient.create('teams',{
      name: team.name,
      slug: team.slug,
      module: team.module,
      enabled: team.enabled !== false,
      custom: true,
      business: businessId
    });
    teamIdBySlug.set(team.slug,created.id);
  }

  const liveTeamBySlug=new Map(
    (dataClient.peek().teams || []).map((team)=> [slugify(team.slug || team.name),team])
  );
  const liveBotBySlug=new Map(
    (dataClient.peek().bots || []).map((bot)=> [slugify(bot.slug || bot.name),bot])
  );

  const existingCount=(dataClient.peek().bots || []).length;
  const botIdBySlug=new Map();
  let index=0;

  for (const bot of plan.newBots) {
    const created=await dataClient.create('bots',{
      name: bot.name,
      slug: bot.slug,
      role: bot.role,
      systemPrompt: bot.system_prompt,
      modelTier: ALIAS_TO_CLIENT[bot.model_tier] || 'tier_balanced',
      teamId: bot.team ? (teamIdBySlug.get(bot.team) || liveTeamBySlug.get(bot.team)?.id || null) : null,
      modelSource: 'platform',
      byoModelId: null,
      agentClass: bot.agent_class,
      tools: [...(bot.tools || [])],
      enabled: bot.enabled !== false,
      isLead: Boolean(bot.is_lead),
      consultableBots: [],
      color: BOT_COLORS[(existingCount + index) % BOT_COLORS.length],
      business: businessId
    });
    botIdBySlug.set(bot.slug,created.id);
    index+=1;
  }

  // Consult wiring (relations → in-memory ids here).
  for (const bot of plan.newBots) {
    if (!bot.consultable_bots.length) continue;
    const consultIds=bot.consultable_bots
      .map((slug)=> botIdBySlug.get(slug) || liveBotBySlug.get(slug)?.id)
      .filter(Boolean);
    if (!consultIds.length) continue;
    await dataClient.update('bots',botIdBySlug.get(bot.slug),{
      consultableBots: consultIds
    });
  }
}
